import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../i18n/I18nContext';
import { usePermissions } from '../hooks/usePermissions';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { ArrowLeft, Download, Copy, Check, QrCode } from 'lucide-react';
import { toast } from 'sonner';

export default function PublishShare() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { canShare } = usePermissions();
  const [course, setCourse] = useState(null);
  const [shareLink, setShareLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchCourse();
  }, [id]);

  const fetchCourse = async () => {
    try {
      const response = await api.get(`/courses/${id}`);
      setCourse(response.data);
      
      if (response.data.status !== 'published') {
        toast.error('Course must be published first');
        navigate(`/courses/${id}/edit`);
        return;
      }
    } catch (error) {
      console.error('Failed to fetch course:', error);
      toast.error('Failed to load course');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const generateLink = async () => {
    setGenerating(true);
    try {
      // Generate only ONE public link
      const linksToCreate = [{}]; // Empty object = public, no filters
      
      const response = await api.post(`/courses/${id}/share`, linksToCreate);
      setShareLink(response.data[0]); // Take the first (and only) link
      toast.success(t('share_copied'));
    } catch (error) {
      console.error('Failed to generate link:', error);
      toast.error('Failed to generate share link');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(t('share_copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Could not copy link');
    }
  };

  const downloadQR = (base64, filename) => {
    const link = document.createElement('a');
    link.href = base64;
    link.download = filename;
    link.click();
    toast.success('QR downloaded!');
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">{t('loading')}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="publish-share">
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-button">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">{t('share_title')}</h1>
              <p className="text-sm text-gray-600">{course?.title}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!shareLink ? (
          <Card data-testid="generate-link-card">
            <CardContent className="py-12 text-center">
              <QrCode className="h-16 w-16 mx-auto mb-4 text-gray-400" />
              <h2 className="text-xl font-bold mb-2">{t('share_qr_title')}</h2>
              <p className="text-gray-600 mb-6">
                {t('share_qr_description')}
                <br />
                <span className="text-sm">{t('share_qr_sub')}</span>
              </p>
              <Button onClick={generateLink} disabled={generating || !canShare} size="lg" data-testid="generate-link-button" style={{ display: canShare ? undefined : 'none' }}>
                {generating ? t('share_generating') : t('share_generate')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Main QR Code Card */}
            <Card data-testid="share-link-card">
              <CardHeader className="text-center">
                <CardTitle className="text-xl">{t('share_link_title')}</CardTitle>
                <CardDescription>
                  {t('share_link_description')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* QR Code - Centered and Large */}
                <div className="flex flex-col items-center">
                  <div className="bg-white p-4 rounded-lg border-2 border-gray-200 shadow-sm">
                    <img
                      src={shareLink.qr_base64}
                      alt="QR Code"
                      className="w-64 h-64"
                      data-testid="qr-code"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-3">
                    {t('share_scan_hint')}
                  </p>
                  <Button
                    variant="outline"
                    className="mt-3"
                    onClick={() => downloadQR(shareLink.qr_base64, `qoldo-${course.title.replace(/\s+/g, '-').toLowerCase()}.png`)}
                    data-testid="download-qr"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {t('share_download_qr')}
                  </Button>
                </div>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-white px-3 text-sm text-gray-500">{t('or')}</span>
                  </div>
                </div>

                {/* URL */}
                <div>
                  <label className="block text-sm font-medium mb-2">{t('share_direct_link')}</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={shareLink.url}
                      readOnly
                      className="flex-1 px-3 py-2 bg-gray-50 border rounded text-sm font-mono"
                      data-testid="link-url"
                    />
                    <Button
                      onClick={() => copyToClipboard(shareLink.url)}
                      data-testid="copy-url"
                    >
                      {copied ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          {t('share_copied')}
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          {t('share_copy')}
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Info box */}
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm">
                  <h4 className="font-medium text-blue-900 mb-1">{t('share_tip_title')}</h4>
                  <p className="text-blue-800">
                    {t('share_tip_text')}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Generate new link option */}
            {canShare && (
              <div className="text-center">
                <Button variant="ghost" onClick={generateLink} disabled={generating}>
                  {t('share_new_link')}
                </Button>
                <p className="text-xs text-gray-500 mt-1">
                  {t('share_old_link_works')}
                </p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
